import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { PassId, PassType, PassShape, Stand } from "../models/PassId";

// ============================================
// PASS IDS
// ============================================

export interface GetPassIdsParams {
	id?: number | null;
	standId?: number | null;
	passTypeId?: number | null;
	passShapeId?: number | null;
	onlyActives?: boolean;
}

export const getPassIds = async (
	params: GetPassIdsParams = {},
): Promise<PassId[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: params.id ?? null },
		{ name: "StandId", value: params.standId ?? null },
		{ name: "PassTypeId", value: params.passTypeId ?? null },
		{ name: "PassShapeId", value: params.passShapeId ?? null },
		{ name: "OnlyActives", value: params.onlyActives ?? true },
	];

	const resp = await apiService.callV2("[MILL].[GetPassIds]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as PassId[];
	}
	throw new Error(resp.message || "Error fetching pass IDs");
};

export const upsertPassId = async (passId: PassId): Promise<PassId> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: passId.Id },
		{ name: "Name", value: passId.Name },
		{ name: "Description", value: passId.Description },
		{ name: "PassTypeId", value: passId.PassTypeId },
		{ name: "PassShapeId", value: passId.PassShapeId },
		{ name: "StandId", value: passId.StandId },
		{ name: "ExtraStandIds", value: passId.ExtraStandIds ?? "" },
		{ name: "ScheduledTonnage", value: passId.ScheduledTonnage },
		{ name: "ScheduledBillets", value: passId.ScheduledBillets },
		{ name: "Active", value: passId.Active },
	];

	const resp = await apiService.callV2("[MILL].[UpsertPassId]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0]", null) as PassId;
	}
	throw new Error(resp.message || "Error saving pass ID");
};

export const deletePassId = async (passIdId: number): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Id", value: passIdId }];

	const resp = await apiService.callV2("[MILL].[RemovePassId]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting pass ID");
	}
};

// ============================================
// PASS TYPES (for dropdown)
// ============================================

export const getPassTypes = async (): Promise<PassType[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: true }];

	const resp = await apiService.callV2("[MILL].[GetPassTypes]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as PassType[];
	}
	throw new Error(resp.message || "Error fetching pass types");
};

// ============================================
// PASS SHAPES (for dropdown)
// ============================================

export const getPassShapes = async (): Promise<PassShape[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: true }];

	const resp = await apiService.callV2("[MILL].[GetPassShapes]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as PassShape[];
	}
	throw new Error(resp.message || "Error fetching pass shapes");
};

// ============================================
// STANDS (for dropdown)
// ============================================

export const getStands = async (): Promise<Stand[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: true }];

	const resp = await apiService.callV2("[MILL].[GetStands]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as Stand[];
	}
	throw new Error(resp.message || "Error fetching stands");
};
